//  src/utils/perfTelemetry.ts
import type { ILogRocket } from '@/services/Logger';
import { METRIC_EVENTS } from '@/utils/metricsConstants';

type MetricStats = {
  count: number;
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  minMs: number;
  maxMs: number;
  lastMs: number;
};

type PerfTelemetryOptions = {
  logger?: ILogRocket;
  enabled: boolean; //  perf flag
  sendToLogRocket: boolean; //  logrocket flag (dev gating)
  env: string; //  dev/qa/prod
  release: string; //  PACKAGE_VERSION or what is in Logger init
  flushEveryMs?: number; //  default 10 minutes
  maxSamplesPerMetric?: number; //  default 100
  debug?: boolean; //  console.debug summaries
};

/**
 * Map Performance.measure names -> stable metric keys for dashboards.
 * Keep these stable even if names change
 */
const MEASURE_TO_METRIC: Record<string, string> = {
  //  Trip Search
  'span:ui:trip-search:grid-ready': 'trip_search.grid_ready',
  'span:ui:trip-search:signalr': 'trip_search.signalr',
  'span:ui:trip-search:dom-ready': 'trip_search.dom_ready',
  //    app initalize
  'span:ui:app:initial-ui': 'app.initial_ui',
  'span:ui:startup:environment.initialize': 'startup.environment_initialize',
  'span:ui:startup:connection.initialize': 'startup.connection_initialize',
  'span:ui:startup:appcontroller.initialize': 'startup.appcontroller_initialize',
  //  Zones
  'span:ui:zones:load': 'zones.load',

  //  Add more as you make them:
  //  'span:ui:pickup-address:geocode': 'pickup_address.geocode',
  //  'span:ui:assignment:load': 'assignment.load',
  //  'span:ui:scheduler:load': 'scheduler.load',

  //  If you also emit app marks/measures, map them too:
  //  'initial-ui': 'app.initial_ui',
  //  'app.initial_load': 'app.initial_load',
};

/**
 * Direct LogRocket track events (not routed through perfTelemetry flush):
 *
 * These metrics are emitted directly via logger.track() and appear as custom events
 * in LogRocket dashboards. They do NOT use performance.measure() or this flush system.
 *
 * ─── Trip Lifecycle ───────────────────────────────────────────────────────────
 *  - trip.create          — Trip creation RTT (SignalR invoke duration)
 *  - trip.create_assign   — Combined create + assign flow duration
 *  - trip.assign_unit     — Unit assignment to trip duration
 *  - trip.stamp           — Status change (stamp) duration with from/to status
 *
 * ─── Address Resolution ──────────────────────────────────────────────────────
 *  - address.autocomplete     — Google Places autocomplete API latency
 *  - address.place_details    — Google Place Details API latency
 *  - address.geocode          — Geocoder class geocode (placeId or address)
 *  - address.geocode_service  — Legacy geocodeService forward/reverse geocode
 *  - GoogleGeocode            — (existing) geocode success with result count + durationMs
 *
 * ─── Resource List / Units ───────────────────────────────────────────────────
 *  - resource_list.initial_load       — Time from app-start to first UnitsMsg
 *  - resource_list.status_update_lag  — Round-trip from stamp request to TripUpdateMsg confirmation
 *
 * ─── AVL (Vehicle Positions) ─────────────────────────────────────────────────
 *  - avl.health           — Periodic (5 min) summary: received, dropped, staleness, dropRate
 *
 * ─── Pending Assignments ─────────────────────────────────────────────────────
 *  - pending_assignments.compute  — Filter/sort computation time (when >20ms)
 *
 * ─── Network ─────────────────────────────────────────────────────────────────
 *  - Network Diagnostics  — Periodic probes, connection API, API timing, likelyCause
 */

const round = (n: number) => Math.round(n);

function percentile(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = values.slice().sort((a, b) => a - b);
  const idx = Math.floor((sorted.length - 1) * p);
  return sorted[idx];
}

function calcStats(samples: number[]): MetricStats {
  const count = samples.length;
  const lastMs = count ? samples[count - 1] : 0;
  const minMs = count ? Math.min(...samples) : 0;
  const maxMs = count ? Math.max(...samples) : 0;
  const avgMs = count ? samples.reduce((a, b) => a + b, 0) / count : 0;

  return {
    count,
    avgMs: round(avgMs),
    p50Ms: round(percentile(samples, 0.5)),
    p95Ms: round(percentile(samples, 0.95)),
    minMs: round(minMs),
    maxMs: round(maxMs),
    lastMs: round(lastMs),
  };
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  }
  catch {
    return null;
  }
}
function safeLocalStorageSet(key: string, val: string): void {
  try {
    window.localStorage.setItem(key, val);
  }
  catch { /* ignore */ }
}

function getOrCreateId(key: string, prefix: string): string {
  const existing = safeLocalStorageGet(key);
  if (existing) return existing;
  const id = `${prefix}_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
  safeLocalStorageSet(key, id);
  return id;
}

export function initPerfTelemetry(opts: PerfTelemetryOptions) {
  const {
    logger,
    enabled,
    sendToLogRocket,
    env,
    release,
    flushEveryMs = 10 * 60 * 1000,
    maxSamplesPerMetric = 100,
    debug = false,
  } = opts;

  if (!enabled) return { flush: () => [], stop: () => {} };

  //  Identify window + page-load
  const windowId = getOrCreateId('perf_window_id', 'w');
  const sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  const samplesByMetric = new Map<string, number[]>();
  let lastMeasureIndex = 0;
  let bucketStartTs = Date.now();

  function readMark(name: string): number | undefined {
    const e = performance.getEntriesByName(name, 'mark')[0] as PerformanceMark | undefined;
    return e ? Math.round(e.startTime) : undefined;
  }

  const scanNewMeasures = () => {
    const measures = performance.getEntriesByType('measure') as PerformanceMeasure[];

    if (lastMeasureIndex > measures.length) lastMeasureIndex = 0;

    for (let i = lastMeasureIndex; i < measures.length; i += 1) {
      const m = measures[i];
      const metricKey = MEASURE_TO_METRIC[m.name];

      //  Ignore unmapped measures
      if (metricKey) {
        const arr = samplesByMetric.get(metricKey) ?? [];
        arr.push(m.duration);

        if (arr.length > maxSamplesPerMetric) {
          arr.splice(0, arr.length - maxSamplesPerMetric);
        }
        samplesByMetric.set(metricKey, arr);

        if (debug) {
          //  eslint-disable-next-line no-console
          console.debug('[perf] measure', { name: m.name, metricKey, ms: Math.round(m.duration) });
        }
      }
    }

    lastMeasureIndex = measures.length;
  };

  const emit = (eventName: string, props: Record<string, string | number | boolean | undefined>) => {
    if (sendToLogRocket && logger) {
      logger.track(eventName, props);
    }
    else if (debug) {
      //  eslint-disable-next-line no-console
      console.debug(`[${eventName}]`, props);
    }
  };

  const flush = (reason: 'interval' | 'pagehide' | 'visibility' | 'manual') => {
    scanNewMeasures();

    if (!samplesByMetric.size) {
      bucketStartTs = Date.now();
      return [];
    }

    const bucketEndTs = Date.now();
    const emitted: Array<Record<string, string | number | boolean | undefined>> = [];

    //  Emit one flat event per metric for dashboard
    for (const [metric, arr] of samplesByMetric.entries()) {
      const s = calcStats(arr);

      const evt: Record<string, string | number | boolean | undefined> = {
        env,
        release,
        windowId,
        sessionId,
        reason,
        bucketStartTs,
        bucketEndTs,
        metric,
        count: s.count,
        avgMs: s.avgMs,
        p50Ms: s.p50Ms,
        p95Ms: s.p95Ms,
        minMs: s.minMs,
        maxMs: s.maxMs,
        lastMs: s.lastMs,
      };

      //  Only attach marks for initial UI Metric (omit entirely for other metrics)
      if (metric === 'app.initial_ui') {
        const appStart = readMark('app-start');
        const appReady = readMark('app-ready');
        if (appStart !== undefined) evt.appStartMarkMs = appStart;
        if (appReady !== undefined) evt.appReadyMarkMs = appReady;
      }

      emit(METRIC_EVENTS.PERF_SUMMARY, evt);
      emitted.push(evt);
    }

    samplesByMetric.clear();
    bucketStartTs = Date.now();
    return emitted;
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush('visibility');
  };

  const onPageHide = () => flush('pagehide');

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  const timer = window.setInterval(() => flush('interval'), flushEveryMs);

  return {
    flush: () => flush('manual'),
    stop: () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    },
  };
}
